import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import DynamicCard from '../cards/DynamicCard';

export default function MessageList({ onCardRespond }) {
  const { state } = useWorkspace();
  const { messages, isProcessing } = state.conversation;
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const prevLengthRef = useRef(messages.length);

  // Track whether user is scrolled near the bottom
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    function handleScroll() {
      const threshold = 120;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
      setIsNearBottom(nearBottom);
      if (nearBottom) setHasNewMessage(false);
    }

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll only when near bottom; show indicator otherwise
  useEffect(() => {
    if (messages.length > prevLengthRef.current) {
      if (isNearBottom) {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        setHasNewMessage(true);
      }
    }
    prevLengthRef.current = messages.length;
  }, [messages.length, isNearBottom]);

  // Always scroll on processing state (typing indicator)
  useEffect(() => {
    if (isProcessing && isNearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isProcessing, isNearBottom]);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setHasNewMessage(false);
  }, []);

  return (
    <div className="message-list" ref={listRef}>
      {messages.map((message) => {
        if (message.type === 'card' && message.cardData) {
          return (
            <DynamicCard
              key={message.id}
              cardData={message.cardData}
              onRespond={onCardRespond}
            />
          );
        }

        return (
          <div
            key={message.id}
            className={`msg msg--${message.sender}`}
          >
            {message.content}
          </div>
        );
      })}

      {isProcessing && (
        <div className="ws-typing-indicator">
          <span />
          <span />
          <span />
        </div>
      )}

      <div ref={bottomRef} />

      {hasNewMessage && (
        <button
          type="button"
          className="message-list__new-msg"
          onClick={scrollToBottom}
        >
          <span className="material-icons">keyboard_arrow_down</span>
          Nieuw bericht
        </button>
      )}
    </div>
  );
}
